// Global tuning knobs. Content tables (heroes, skills, biomes) live beside this file.
export const BALANCE = {
  monstersPerStage: 10,
  bossTimeSec: 30,
  bossHpMult: 6,
  bossGoldMult: 5,
  monsterRespawnSec: 0.4,
  /** Overkill carries into following monsters (efficiency = share kept, maxChain = kills per hit). */
  splash: { efficiency: 1, maxChain: 25 },

  /** Monster HP = base * product of per-stage growth. Growth steps up at deeper bands: each step is
   *  a soft wall that sets how long that band of the game takes (tune with `npm run sim`). */
  monsterHp: {
    base: 12,
    bands: [
      { fromStage: 1, growth: 1.39 },
      { fromStage: 41, growth: 1.19 },
      { fromStage: 300, growth: 1.20 },
      { fromStage: 700, growth: 1.21 },
      { fromStage: 1500, growth: 1.22 },
    ],
  },
  monsterGold: { hpRatio: 0.09, flat: 3 },

  critChance: 0.05,
  critMult: 5,

  /** Share of total hero DPS added to each tap, so tapping stays relevant. */
  tapDpsShare: 0.04,
  tapCost: { base: 8, growth: 1.074 },
  /** tap = base * (1 + level) * perLevelGrowth^level. perLevelGrowth vs tapCost.growth sets how
   *  tap damage scales with gold (ln 1.025 / ln 1.074 ≈ 0.35, same ballpark as heroes). Keep it
   *  well below 1 or taps outrun monster HP by themselves. `base` shifts early power only. */
  tapDamage: { base: 4, perLevelGrowth: 1.025 },

  heroCostGrowth: 1.07,
  heroMilestoneEvery: 25, // x2 hero DPS every N levels

  /** relics = floor(coef * (stage - offset)^power * growth^stage). Exponential in stage so
   *  deeper pushes keep paying off; artifacts turn relics into damage. */
  //  Tuned so each decade of relics (≈7.8 decades of damage via artifacts) pushes a bit less than
  //  one HP band decade: every ascension goes further, with slowly diminishing gains.
  //  Past `lateFrom`, Stardust grows by `lateGrowth` per stage instead, so pushing deeper keeps paying
  //  enough to beat the HP bands: the endless loop (owner, 2026-10-16). Simulated over 87h with Stars:
  //  +50-80 stages per Rewind with no wall. 1.013+ runs away (+1,000 stages a Rewind); keep it at 1.01.
  prestige: { minStage: 30, offset: 20, coef: 1, power: 1.0, growth: 1.003, lateFrom: 1200, lateGrowth: 1.01 },
  /**
   * Coins feeding long-term power, since Gems buy coins only (owner, 2026-10-15). `stardustPerDecade`:
   * a Rewind's Stardust x (1 + this x log10(1 + coins bought this run / 1,000 enemies' worth)); with
   * `paidOnly` 1 only coins bought with purchased Gems count, so free players play exactly as before.
   * Chosen at 2 from simulations: EUR 10/week ~ +22% stages at 24h, EUR 10/day ~ +42%.
   * The Treasure Forge (`forge*`) is an EXPERIMENT, off at 0. `forgeDamagePerLevel`: the Treasure Forge turns coins into permanent
   * all-damage levels; level L costs the gold of `forgeKills` enemies at stage forgeBaseStage + L x
   * forgeStagesPerLevel. Simulate with `npm run sim -- --set coinPower.<key>=<value>`.
   */
  coinPower: { stardustPerDecade: 2, paidOnly: 1, forgeDamagePerLevel: 0, forgeKills: 1000, forgeBaseStage: 60, forgeStagesPerLevel: 6 },

  /**
   * Guardian Stars (endless power from the existing roster): at a Rewind, every Guardian that reached
   * level firstLevel + stars x levelStep earns a star (several at once if it got far enough). Stars are
   * permanent and each multiplies that Guardian's damage by `mult`. 0 mult = off. With the late Stardust
   * growth they keep the climb steady instead of slowly fading (tools/sim.ts, 2026-10-16).
   */
  stars: { firstLevel: 1000, levelStep: 500, mult: 10 },

  /** Exponent on each artifact's all-damage rider. Riders multiply across every artifact, so
   *  this controls how hard the collection compounds (1 = full TT2-style stacking). */
  artifactRiderPower: 0.5,

  offline: { capSec: 8 * 3600, efficiency: 0.5, minSec: 60 },
} as const;
